import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {  useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";


import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { DiarioObrasAtividadesForm, DiarioObrasAtividadesSchema } from "../../../schemas/DiarioObrasAtividades/diario_obras_atividades";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";


type Props = {
  open: boolean,
  saving: boolean,
  selectedAtividades: DiarioObrasAtividadesForm | null,
  IdDiarioObras: number;
  onSubmit: (form: DiarioObrasAtividadesForm) => void,
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
  onClose: () => void,
};

export const AtividadesModal = ({
  open,
  saving,
  IdDiarioObras,
  selectedAtividades,
  onOpenChangeHistory,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<DiarioObrasAtividadesForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasAtividadesSchema),
  });

    const tipoMaoDeObraAValue = hookForm.watch("TipoMaoDeObra");
  
  useEffect(() => {
    if (Number(tipoMaoDeObraAValue) === 0) {
      hookForm.setValue("IdFornecedor", null);
    }
  }, [hookForm, tipoMaoDeObraAValue]);

  useEffect(() => {
    if (open) {
      if (selectedAtividades) {
        hookForm.reset({
          IdDiarioObrasAtividades: selectedAtividades.IdDiarioObrasAtividades,
          IdDiarioObras: selectedAtividades.IdDiarioObras,
          Descricao: selectedAtividades.Descricao,
          IdFornecedor: selectedAtividades.IdFornecedor,
          Ordem: selectedAtividades.Ordem,
          TipoMaoDeObra: selectedAtividades.TipoMaoDeObra,
        });
      }
      } else {
        hookForm.reset({
          IdDiarioObrasAtividades: undefined,
          IdDiarioObras: IdDiarioObras,
          Descricao: "",
          IdFornecedor: null,
          Ordem: null,
          TipoMaoDeObra: 0,
        });
      }
  }, [hookForm, open, IdDiarioObras, selectedAtividades]);

  const handleChangeHistory = useCallback(() => {
    if (selectedAtividades) {
      onOpenChangeHistory(selectedAtividades.IdDiarioObrasAtividades, LogTipoTabela.DIARIO_OBRAS_ATIVIDADES);
    }
  }, [selectedAtividades, onOpenChangeHistory]);

  const handleSubmit = useCallback((form: DiarioObrasAtividadesForm) => {
    if (!saving){
      onSubmit(form);
      // hookForm.reset(); 
    } 
  }, [onSubmit, saving]);

  const handleClose = useCallback(() => {
    if (!saving){
      onClose();
      hookForm.reset(); 
    } 
  }, [hookForm, onClose, saving]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
       sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "80rem",
          },
        },
      }}
      >
      <DialogTitle>
        Atividades Realizadas
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={selectedAtividades ? "Editar" : "Cadastrar"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name='Ordem'
                label="Ordem"
                multiline={true}
                reactHookForm={hookForm}
              />
              <DefaultFormInput
                  name='Descricao'
                  label="Atividades"
                  multiline={true}
                  rows={4}
                  reactHookForm={hookForm}
                />

              <FormRadioGroupInput
                name="TipoMaoDeObra"
                label="Tipo de mão de Obra"
                readOnly={saving}
                options={[
                  { id: 0, label: "Própria" },
                  { id: 1, label: "Terceirizada" },
                ]}
                reactHookForm={hookForm}
              />

              {Number(tipoMaoDeObraAValue) === 1 && (
                  <Box
                    sx={{
                      position: 'relative',
                      '& .MuiAutocomplete-popper': {
                        position: 'absolute !important',
                        top: '100% !important',     
                        bottom: 'auto !important',   
                        transform: 'none !important', 
                        zIndex: 9999,                
                      },
                      '& .MuiAutocomplete-listbox': {
                        maxHeight: '200px !important',
                      }
                    }}
                  >
                    <DropDownFornecedor
                      name="IdFornecedor"
                      label="Fornecedor:"
                      required={true}
                      readOnly={saving}
                      
                      reactHookForm={hookForm}
                      />
                  </Box>
              )}
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {
              isNotNullOrUndefined(selectedAtividades) &&
              <PrimaryButton
              buttonColor="yellow"
              onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}